import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Clock, CheckCircle, AlertCircle, Search, Package, Calendar, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import { isOpsManager, isWarehouseUser } from './accessControl.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import { storage, ref, uploadBytes, getDownloadURL } from './firebaseUtils.js';
import StatusBadge from './components/StatusBadge.js';

const getPackingProofFiles = (mocData = {}) => {
  if (Array.isArray(mocData.packingProofFiles)) return mocData.packingProofFiles;
  if (!mocData.packingImageUrl) return [];

  return [{
    name: 'Final Packing Image',
    url: mocData.packingImageUrl,
    uploadedAt: mocData.packedAt || mocData.updatedAt || '',
    type: 'image'
  }];
};

const PackingList = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [scheduleDateFrom, setScheduleDateFrom] = useState('');
  const [scheduleDateTo, setScheduleDateTo] = useState('');
  const [selectedMoc, setSelectedMoc] = useState(null);
  const [packingProofFiles, setPackingProofFiles] = useState([]);
  const [packingRemarks, setPackingRemarks] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    const packingStatuses = isWarehouseUser(user)
      ? ["packing_scheduled", "packing", "packed_ready", "packing_hold", "packing_rejected"]
      : ["packing_queued", "packing_scheduled", "packing", "packed_ready", "packing_hold", "packing_rejected"];

    // Fetch MOCs that are in packing, ready to be scheduled, on hold, or rejected
    const q = query(
      collection(db, "dispatches"), 
      where("dispatchStatus", "in", packingStatuses)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log("Packing Queue Snapshot received. Count:", querySnapshot.size);
      const mocList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setMocs(mocList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Packing list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db, user]);

  const renderStatus = (status) => {
    switch (status) {
      case 'packing_queued':
        return <StatusBadge status="packing_queued" />;
      case 'packing_scheduled':
        return <StatusBadge status="packing_scheduled" />;
      case 'packing':
        return <span style={{ color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Package size={14} /> Packing</span>;
      case 'packing_hold':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Clock size={14} /> Hold</span>;
      case 'packing_rejected':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><AlertCircle size={14} /> Rejected</span>;
      case 'packed_ready':
        return <StatusBadge status="packed_ready" />;
      case 'invoice_pending':
        return <StatusBadge status="invoice_pending" />;
      case 'ewaybill_pending':
        return <StatusBadge status="ewaybill_pending" />;
      case 'invoice_ready':
        return <StatusBadge status="invoice_ready" />;
      case 'ewaybill_complete':
        return <StatusBadge status="ewaybill_complete" />;
      case 'dc_confirmed':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><CheckCircle size={14} /> QC Pending</span>;
      default:
        return <span style={{ color: '#64748b' }}>{status?.replace(/_/g, ' ')}</span>;
    }
  };

  const warehouseOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.warehouse).filter(Boolean)))], [mocs]);
  const statusOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.dispatchStatus).filter(Boolean)))], [mocs]);

  const groupedMocs = useMemo(() => {
    const list = mocs.filter(moc => {
      if (moc.dispatchStatus === 'delivered') return false;

      const matchesSearch = 
        moc.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.mocNo?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesWarehouse = filterWarehouse === 'All' || moc.warehouse === filterWarehouse;
      const matchesStatus = filterStatus === 'All' || moc.dispatchStatus === filterStatus;
      const scheduledDate = moc.scheduledDate || '';
      const matchesScheduleDateFrom = !scheduleDateFrom || (scheduledDate && scheduledDate >= scheduleDateFrom);
      const matchesScheduleDateTo = !scheduleDateTo || (scheduledDate && scheduledDate <= scheduleDateTo);

      return matchesSearch && matchesWarehouse && matchesStatus && matchesScheduleDateFrom && matchesScheduleDateTo;
    });

    // Sort by Date, then Sequence
    list.sort((a, b) => {
        const dateA = a.scheduledDate || '9999-12-31';
        const dateB = b.scheduledDate || '9999-12-31';
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;

        const seqA = (a.sequence === undefined || a.sequence === null || a.sequence === 0) ? 999999 : a.sequence;
        const seqB = (b.sequence === undefined || b.sequence === null || b.sequence === 0) ? 999999 : b.sequence;
        return seqA - seqB;
    });

    // Group by Date
    const groups = {};
    list.forEach(moc => {
        const dateKey = moc.scheduledDate || 'Not Set';
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(moc);
    });

    // Return sorted array of groups to guarantee chronological order when rendering
    return Object.keys(groups).sort((a, b) => {
        if (a === 'Not Set') return 1;
        if (b === 'Not Set') return -1;
        return a.localeCompare(b);
    }).map(dateKey => ({
        dateKey,
        items: groups[dateKey]
    }));
  }, [mocs, searchTerm, filterWarehouse, filterStatus, scheduleDateFrom, scheduleDateTo]);

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'High': return { color: '#ef4444', backgroundColor: '#fee2e2' };
      case 'Medium': return { color: '#f59e0b', backgroundColor: '#fef3c7' };
      case 'Low': return { color: '#10b981', backgroundColor: '#d1fae5' };
      default: return { color: '#6b7280', backgroundColor: '#f3f4f6' };
    }
  };

  const formatDate = (dateString) => {
      if (!dateString) return 'Not Set';
      const resolvedDate = dateString?.toDate ? dateString.toDate() : dateString;
      // Handle "YYYY-MM-DD" from the date input
      if (typeof resolvedDate === 'string' && resolvedDate.length === 10) {
        return new Date(resolvedDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
      }

      const parsedDate = new Date(resolvedDate);
      if (Number.isNaN(parsedDate.getTime())) return 'Invalid Date';

      return parsedDate.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateHeader = (dateString) => {
      if (!dateString || dateString === 'Not Set') return 'Unscheduled / Not Set';
      const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const formatTime = (dateValue) => {
      if (!dateValue) return '';

      const resolvedDate = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
      if (Number.isNaN(resolvedDate.getTime())) return '';

      return resolvedDate.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit'
      });
  };

  const renderDateTimeCell = (dateValue, timeValue, emptyLabel = '--') => {
      const formattedDate = dateValue ? formatDate(dateValue) : emptyLabel;
      const formattedTime = formatTime(timeValue);

      return (
        <>
          <div>{formattedDate}</div>
          {formattedTime && (
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
              {formattedTime}
            </div>
          )}
        </>
      );
  };

  const openPackingDoneModal = (moc) => {
    setSelectedMoc(moc);
    setPackingProofFiles(getPackingProofFiles(moc));
    setPackingRemarks(moc.packingRemarks || '');
  };

  const closePackingDoneModal = (forceClose = false) => {
    if (!forceClose && (isUpdating || uploading)) return;
    setSelectedMoc(null);
    setPackingProofFiles([]);
    setPackingRemarks('');
  };

  const uploadPackingProofFiles = async (mocId) => {
    const existingFiles = packingProofFiles
      .filter((file) => file.url)
      .map((file) => ({
        name: file.name || 'Packing proof',
        url: file.url,
        size: file.size || 0,
        type: file.type || '',
        uploadedAt: file.uploadedAt || ''
      }));

    const newFiles = packingProofFiles.filter((file) => !file.url);
    if (newFiles.length === 0) return existingFiles;

    setUploading(true);
    try {
      const uploadedFiles = await Promise.all(newFiles.map(async (file, index) => {
        const path = `packing_images/${mocId}/${Date.now()}_${index}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        return {
          name: file.name,
          url,
          size: file.size || 0,
          type: file.type || '',
          uploadedAt: new Date().toISOString()
        };
      }));

      return [...existingFiles, ...uploadedFiles];
    } finally {
      setUploading(false);
    }
  };

  const handlePackingDoneConfirm = async () => {
    if (!selectedMoc) return;

    setIsUpdating(true);
    try {
      const timestamp = serverTimestamp();
      const uploadedPackingProofFiles = await uploadPackingProofFiles(selectedMoc.id);
      const updates = {
        dispatchStatus: 'packed_ready',
        sequence: 0,
        packingRemarks: packingRemarks || '',
        packingProofFiles: uploadedPackingProofFiles,
        packingImageUrl: uploadedPackingProofFiles[0]?.url || '',
        packedAt: timestamp,
        updatedAt: timestamp
      };

      await updateDoc(doc(db, "dispatches", selectedMoc.id), updates);

      if (selectedMoc.orderId) {
        await updateDoc(doc(db, "orders", selectedMoc.orderId), {
          status: 'packed_ready',
          updatedAt: timestamp
        });
      }

      alert("Packing Complete! Record locked and moved to DC stage.");
      closePackingDoneModal(true);
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const getActionConfig = (moc) => {
    const isOpsOrOpsHead = user?.role === 'ops' || isOpsManager(user);
    const isWarehouseRole = isWarehouseUser(user);

    if (isWarehouseRole && moc.dispatchStatus === 'packing_scheduled') {
      return {
        label: 'Packing Done',
        style: packingDoneBtnStyle,
        onClick: () => openPackingDoneModal(moc)
      };
    }

    if (isOpsOrOpsHead && moc.dispatchStatus === 'packing_queued') {
      return {
        label: 'Set Schedule',
        style: scheduleActionBtnStyle,
        onClick: () => navigate(`/ops/packing/${moc.id}`)
      };
    }

    return {
      label: 'Preview',
      style: previewActionBtnStyle,
      onClick: () => navigate(`/ops/packing/${moc.id}`)
    };
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Packaging & Staging</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Schedule and manage material packing sequences.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div style={filterSectionStyle}>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Customer Search</label>
          <div style={searchFieldWrapStyle}>
            <Search style={searchIconStyle} size={18} />
            <input 
              type="text" 
              placeholder="Search by Customer or MOC No..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={searchInputStyle}
            />
          </div>
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Schedule From</label>
          <input
            type="date"
            value={scheduleDateFrom}
            onChange={(e) => setScheduleDateFrom(e.target.value)}
            style={filterInputStyle}
          />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Schedule To</label>
          <input
            type="date"
            value={scheduleDateTo}
            onChange={(e) => setScheduleDateTo(e.target.value)}
            style={filterInputStyle}
          />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={filterInputStyle}
          >
            {statusOptions.map((option) => (
              <option key={option} value={option}>{option === 'All' ? 'All Statuses' : option}</option>
            ))}
          </select>
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Warehouse</label>
          <select 
            value={filterWarehouse}
            onChange={(e) => setFilterWarehouse(e.target.value)}
            style={filterInputStyle}
          >
            {warehouseOptions.map((option) => (
              <option key={option} value={option}>{option === 'All' ? 'All Warehouses' : option}</option>
            ))}
          </select>
        </div>
      </div>

      {groupedMocs.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Package size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>Queue is Clear!</h3>
          <p style={{ color: '#64748b' }}>No MOCs are currently awaiting packing.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          {groupedMocs.map((group) => (
            <div key={group.dateKey}>
              <h3 style={{ fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                <Calendar size={18} color="#2563eb" /> 
                {formatDateHeader(group.dateKey)}
                <span style={{ fontSize: '12px', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px', marginLeft: '5px' }}>
                  {group.items.length}
                </span>
              </h3>
              <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th style={thStyle}>Seq</th>
                      <th style={thStyle}>Priority</th>
                      <th style={{...thStyle, textAlign:'left'}}>Customer / MOC</th>
                      <th style={thStyle}>Scheduled Date</th>
                      <th style={thStyle}>Packed On</th>
                      <th style={{...thStyle, textAlign:'left'}}>Remarks</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Warehouse</th>
                      <th style={thStyle}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((moc) => {
                      const actionConfig = getActionConfig(moc);

                      return (
                      <tr 
                        key={moc.id} 
                        style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                        onClick={() => navigate(`/ops/packing/${moc.id}`)}
                        className="hover-highlight"
                      >
                        <td style={{...tdStyle, fontWeight: 'bold', color: '#2563eb'}}>{(moc.sequence === 0 || !moc.sequence) ? '--' : `#${moc.sequence}`}</td>
                        <td style={tdStyle}>
                          <span style={{ 
                              padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold',
                              ...getPriorityStyle(moc.priority)
                          }}>
                              {moc.priority || 'NORMAL'}
                          </span>
                        </td>
                        <td style={{...tdStyle, textAlign:'left'}}>
                          <div style={{fontWeight: 'bold', fontSize: '14px'}}>{moc.customerName}</div>
                          <div style={{fontSize: '11px', color: '#6b7280'}}>MOC: {moc.mocNo}</div>
                        </td>
                        <td style={tdStyle}>{renderDateTimeCell(moc.scheduledDate, moc.packingScheduledAt, 'Not Set')}</td>
                        <td style={tdStyle}>{renderDateTimeCell(moc.packedAt, moc.packedAt, '--')}</td>
                        <td style={{...tdStyle, textAlign:'left', fontSize: '12px', color: '#4b5563', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {moc.dispatchStatus === 'packed_ready' ? (moc.packingRemarks || '--') : (moc.warehouseRemarks || '--')}
                        </td>
                        <td style={tdStyle}>
                          {renderStatus(moc.dispatchStatus)}
                        </td>
                        <td style={tdStyle}>
                          <span style={warehouseBadgeStyle}>
                            {moc.warehouse || '--'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              actionConfig.onClick();
                            }}
                            style={actionConfig.style}
                          >
                            {actionConfig.label}
                          </button>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedMoc && (
        <div style={modalOverlayStyle}>
          <div style={modalCardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#059669' }}>Complete Packing</h3>
              <button
                onClick={closePackingDoneModal}
                disabled={isUpdating || uploading}
                style={{ background: 'none', border: 'none', cursor: isUpdating || uploading ? 'not-allowed' : 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Are you sure packing is complete for <strong>{selectedMoc.mocNo}</strong>? You can optionally upload a final packing photo and add remarks.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={filterLabelStyle}>Final Packing Image / Files (Optional)</label>
                <MultiFileUpload
                  value={packingProofFiles}
                  onFilesChange={setPackingProofFiles}
                  multiple={true}
                  maxSizeMB={25}
                  helperText="Upload one or more final packing proof files. Total size limit is 25MB."
                  uploading={uploading}
                />
              </div>

              <div>
                <label style={filterLabelStyle}>Packing Remarks (Optional)</label>
                <textarea
                  value={packingRemarks}
                  onChange={(e) => setPackingRemarks(e.target.value)}
                  style={{ ...filterInputStyle, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Add notes about damage, missing items, or packing condition..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={closePackingDoneModal} disabled={isUpdating || uploading} style={cancelBtnStyle}>Cancel</button>
              <button onClick={handlePackingDoneConfirm} disabled={isUpdating || uploading} style={packingDoneBtnStyle}>
                {isUpdating ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Packing Done'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };
const warehouseBadgeStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', maxWidth: '160px', padding: '5px 10px', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const previewActionBtnStyle = { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '12px', cursor: 'pointer' };
const scheduleActionBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' };
const packingDoneBtnStyle = { backgroundColor: '#059669', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' };
const cancelBtnStyle = { backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' };

const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const searchFieldWrapStyle = {
  position: 'relative',
  width: '100%'
};
const searchInputStyle = { width: '100%', padding: '8px 8px 8px 36px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const searchIconStyle = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 };
const modalCardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '500px', maxWidth: '90%' };

export default PackingList;
